import { useState, useEffect } from '@wordpress/element';

// ─────────────────────────────────────────────────────────────────────────────
// Field type catalogue — mirrors cfafwr_admin_metabox.php exactly
// ─────────────────────────────────────────────────────────────────────────────
const FIELD_TYPES = [
  {
    group: 'Billing Address Fields',
    options: [
      { value: 'billing_first_name',  label: 'First Name (Billing)' },
      { value: 'billing_last_name',   label: 'Last Name (Billing)' },
      { value: 'billing_company',     label: 'Company (Billing)' },
      { value: 'billing_address_1',   label: 'Address Line 1 (Billing)' },
      { value: 'billing_address_2',   label: 'Address Line 2 (Billing)' },
      { value: 'billing_city',        label: 'City (Billing)' },
      { value: 'billing_state',       label: 'State / County (Billing)' },
      { value: 'billing_postcode',    label: 'Postcode / ZIP  (Billing)' },
      { value: 'billing_country',     label: 'Country (Billing)' },
      { value: 'billing_phone',       label: 'Phone (Billing)' },
      { value: 'billing_email',       label: 'Email Address (Billing)' },
    ],
  },
  {
    group: 'Shipping Address Fields',
    options: [
      { value: 'shipping_first_name', label: 'First Name (Shipping)' },
      { value: 'shipping_last_name',  label: 'Last Name (Shipping)' },
      { value: 'shipping_company',    label: 'Company (Shipping)' },
      { value: 'shipping_address_1',  label: 'Address Line 1 (Shipping)' },
      { value: 'shipping_address_2',  label: 'Address Line 2 (Shipping)' },
      { value: 'shipping_city',       label: 'City (Shipping)' },
      { value: 'shipping_state',      label: 'State / County (Shipping)' },
      { value: 'shipping_postcode',   label: 'Postcode / ZIP (Shipping)' },
      { value: 'shipping_country',    label: 'Country (Shipping)' },
    ],
  },
  {
    group: 'Other Fields',
    options: [
      { value: 'text',          label: 'Text' },
      { value: 'number',        label: 'Number' },
      { value: 'tel',           label: 'Phone' },
      { value: 'email',         label: 'Email' },
      { value: 'url',           label: 'URL' },
      { value: 'image',         label: 'File Upload', pro: true },
      { value: 'password',      label: 'Password', pro: true },
      { value: 'textarea',      label: 'Textarea', pro: true },
      { value: 'html',          label: 'Custom HTML', pro: true },
      { value: 'color',         label: 'Color Picker', pro: true },
      { value: 'time',          label: 'Time Picker', pro: true },
      { value: 'date',          label: 'Date Picker', pro: true },
      { value: 'radio',         label: 'Radio', pro: true },
      { value: 'checkbox',      label: 'Checkbox', pro: true },
      { value: 'multicheckbox', label: 'Multiple Checkbox', pro: true },
      { value: 'select',        label: 'Select', pro: true },
      { value: 'multiselect',   label: 'Multi Select', pro: true },
      { value: 'hidden',        label: 'Hidden', pro: true },
    ],
  },
];

// ── Visibility rules — mirrors cfafwr_backend_js.js logic exactly ─────────────
const isBillingOrShipping = (t) => t.startsWith('billing_') || t.startsWith('shipping_');
const needsLabelAndSlug   = (t) => ['text','number','email','url','tel','image','password','textarea','color','time','date','radio','checkbox','multicheckbox','select','multiselect','hidden'].includes(t);
const needsOptions        = (t) => ['radio','select','multiselect','multicheckbox'].includes(t);
const needsPlaceholder    = (t) => ['text','number','email','tel','url','password','textarea'].includes(t);
const isHtmlType          = (t) => t === 'html';
const needsClass          = (t) => !isBillingOrShipping(t);
const needsExtraHtml      = (t) => !isHtmlType(t) && !isBillingOrShipping(t);

const slugify = (str) => str.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');

const BLANK_FORM = {
  post_title:                '',
  custom_field_checkbox:     'yes',
  custom_register_field_type:'text',
  custom_field_label:        '',
  custom_field_slug_name:    '',
  custom_field_required:     '',
  custom_field_placeholder:  '',
  cfafwr_custom_html:        '',
  cfafwr_add_custom_class:   '',
  custom_field_option_value: [''],
  custom_field_option_label: [''],
};

// ─────────────────────────────────────────────────────────────────────────────
// Shared small atoms
// ─────────────────────────────────────────────────────────────────────────────
function Toggle({ id, checked, onChange }) {
  return (
    <span className="cf-toggle">
      <input id={id} type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <span
        className="cf-toggle__slider"
        role="button"
        tabIndex={0}
        aria-pressed={checked}
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); onChange(!checked); }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onChange(!checked);
          }
        }}
      />
    </span>
  );
}

function Lbl({ htmlFor, required, optional, children }) {
  return (
    <label htmlFor={htmlFor} style={{ display:'block', fontSize:12.5, fontWeight:600, color:'var(--cf-text-primary,#1a1f36)', marginBottom:6, lineHeight:1.3 }}>
      {children}
      {required && <span style={{ color:'#e5484d', marginLeft:3 }}>*</span>}
      {optional && <span style={{ fontSize:11, fontWeight:400, color:'var(--cf-text-muted,#8c96b5)', marginLeft:5 }}>optional</span>}
    </label>
  );
}

function Err({ msg }) {
  if (!msg) return null;
  return (
    <p style={{ margin:'5px 0 0', fontSize:12, color:'#e5484d', display:'flex', alignItems:'center', gap:4 }}>
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
        <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
      </svg>
      {msg}
    </p>
  );
}

// Spinner SVG
function Spin() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"
      style={{ animation:'cfm-spin .7s linear infinite', display:'block' }}>
      <line x1="12" y1="2" x2="12" y2="6"/><line x1="12" y1="18" x2="12" y2="22"/>
      <line x1="4.93" y1="4.93" x2="7.76" y2="7.76"/><line x1="16.24" y1="16.24" x2="19.07" y2="19.07"/>
      <line x1="2" y1="12" x2="6" y2="12"/><line x1="18" y1="12" x2="22" y2="12"/>
      <line x1="4.93" y1="19.07" x2="7.76" y2="16.24"/><line x1="16.24" y1="7.76" x2="19.07" y2="4.93"/>
    </svg>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// FieldModal — unified Add + Edit modal
//
// Props:
//   mode          : 'add' | 'edit'
//   editPostId    : number (required when mode === 'edit')
//   onClose       : () => void
//   onFieldCreated: (field) => void   (called after successful add)
//   onFieldUpdated: (field) => void   (called after successful edit)
// ─────────────────────────────────────────────────────────────────────────────
export default function FieldModal({ mode = 'add', editPostId = null, onClose, onFieldCreated, onFieldUpdated }) {
  const isEdit = mode === 'edit';

  const [form,        setForm]        = useState(BLANK_FORM);
  const [errors,      setErrors]      = useState({});
  const [saving,      setSaving]      = useState(false);
  const [loadingData, setLoadingData] = useState(isEdit);  // true while fetching existing meta
  const [slugTouched, setSlugTouched] = useState(isEdit);  // in edit mode slug is always "touched"

  const ft = form.custom_register_field_type;

  // ── Fetch existing meta when in edit mode ─────────────────────────────────
  useEffect(() => {
    if (!isEdit || !editPostId) return;
    setLoadingData(true);

    const fd = new FormData();
    fd.append('action',  'cfafwr_get_field');
    fd.append('nonce',   CFAFWR_DATA.getFieldNonce);
    fd.append('post_id', editPostId);

    fetch(CFAFWR_DATA.ajaxurl, { method: 'POST', body: fd })
      .then((r) => r.json())
      .then((data) => {
        if (data.success) {
          const f = data.data.field;
          setForm({
            post_title:                f.post_title                 || '',
            custom_field_checkbox:     f.custom_field_checkbox      || 'yes',
            custom_register_field_type:f.custom_register_field_type || 'text',
            custom_field_label:        f.custom_field_label         || '',
            custom_field_slug_name:    f.custom_field_slug_name     || '',
            custom_field_required:     f.custom_field_required      || '',
            custom_field_placeholder:  f.custom_field_placeholder   || '',
            cfafwr_custom_html:        f.cfafwr_custom_html         || '',
            cfafwr_add_custom_class:   f.cfafwr_add_custom_class    || '',
            custom_field_option_value: Array.isArray(f.custom_field_option_value) && f.custom_field_option_value.length ? f.custom_field_option_value : [''],
            custom_field_option_label: Array.isArray(f.custom_field_option_label) && f.custom_field_option_label.length ? f.custom_field_option_label : [''],
          });
        } else {
          setErrors({ _global: data.data?.message || 'Could not load field data.' });
        }
      })
      .catch(() => setErrors({ _global: 'Network error — could not load field data.' }))
      .finally(() => setLoadingData(false));
  }, [isEdit, editPostId]);

  // ── Escape key closes ─────────────────────────────────────────────────────
  useEffect(() => {
    const h = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [onClose]);

  // ── Form helpers ──────────────────────────────────────────────────────────
  const set = (key, val) => {
    setForm((f) => ({ ...f, [key]: val }));
    setErrors((e) => ({ ...e, [key]: '', _global: '' }));
  };

  const handleLabelChange = (val) => {
    setForm((f) => ({
      ...f,
      custom_field_label:     val,
      post_title:             (!f.post_title || f.post_title === f.custom_field_label) ? val : f.post_title,
      custom_field_slug_name: slugTouched ? f.custom_field_slug_name : slugify(val),
    }));
    setErrors((e) => ({ ...e, custom_field_label:'', custom_field_slug_name:'', _global:'' }));
  };

  const handleSlugChange = (val) => {
    const clean = val.replace(/[^a-z0-9_]/gi, '').replace(/\s+/g, '_');
    setSlugTouched(true);
    set('custom_field_slug_name', clean);
  };

  // ── Options ───────────────────────────────────────────────────────────────
  const addOption = () => setForm((f) => ({
    ...f,
    custom_field_option_value: [...f.custom_field_option_value, ''],
    custom_field_option_label: [...f.custom_field_option_label, ''],
  }));

  const removeOption = (i) => setForm((f) => ({
    ...f,
    custom_field_option_value: f.custom_field_option_value.filter((_, idx) => idx !== i),
    custom_field_option_label: f.custom_field_option_label.filter((_, idx) => idx !== i),
  }));

  const setOption = (i, key, val) => setForm((f) => {
    const arr = [...f[key]]; arr[i] = val;
    return { ...f, [key]: arr };
  });

  // ── Validate ──────────────────────────────────────────────────────────────
  const validate = () => {
    const e = {};
    if (!form.post_title.trim()) e.post_title = 'Field title is required.';
    if (needsLabelAndSlug(ft)) {
      if (!form.custom_field_label.trim())     e.custom_field_label     = 'Field label is required.';
      if (!form.custom_field_slug_name.trim()) e.custom_field_slug_name = 'Slug name is required.';
      if (['wp_capabilities','wp_user_level'].includes(form.custom_field_slug_name))
        e.custom_field_slug_name = `Slug "${form.custom_field_slug_name}" is not allowed.`;
    }
    if (isHtmlType(ft) && !form.cfafwr_custom_html.trim())
      e.cfafwr_custom_html = 'Custom HTML content is required.';
    return e;
  };

  // ── Submit ────────────────────────────────────────────────────────────────
  const handleSave = () => {
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }

    setSaving(true);
    const fd = new FormData();

    if (isEdit) {
      fd.append('action',  'cfafwr_update_field');
      fd.append('nonce',   CFAFWR_DATA.updateFieldNonce);
      fd.append('post_id', editPostId);
    } else {
      fd.append('action', 'cfafwr_create_field');
      fd.append('nonce',  CFAFWR_DATA.createFieldNonce);
    }

    const scalars = [
      'post_title','custom_field_checkbox','custom_register_field_type',
      'custom_field_label','custom_field_slug_name','custom_field_required',
      'custom_field_placeholder','cfafwr_custom_html','cfafwr_add_custom_class',
    ];
    scalars.forEach((k) => fd.append(k, form[k] || ''));
    form.custom_field_option_value.forEach((v) => fd.append('custom_field_option_value[]', v));
    form.custom_field_option_label.forEach((v) => fd.append('custom_field_option_label[]', v));

    fetch(CFAFWR_DATA.ajaxurl, { method: 'POST', body: fd })
      .then((r) => r.json())
      .then((data) => {
        if (data.success) {
          if (isEdit) onFieldUpdated(data.data.field);
          else        onFieldCreated(data.data.field);
          onClose();
        } else {
          const msg = data.data?.message || 'Could not save field.';
          const fld = data.data?.field;
          if (fld) setErrors({ [fld]: msg });
          else     setErrors({ _global: msg });
        }
      })
      .catch(() => setErrors({ _global: 'Network error — could not save field.' }))
      .finally(() => setSaving(false));
  };

  // ── Shared input styles ───────────────────────────────────────────────────
  const iStyle = (key) => ({
    width:'100%', padding:'9px 12px',
    background:'var(--cf-surface,#fff)',
    border:`1.5px solid ${errors[key] ? '#e5484d' : 'var(--cf-border,#e4e7ef)'}`,
    borderRadius:7, fontSize:13.5,
    fontFamily:'var(--cf-font,inherit)',
    color:'var(--cf-text-primary,#1a1f36)',
    outline:'none',
    transition:'border-color .15s,box-shadow .15s',
    boxSizing:'border-box',
    boxShadow: errors[key] ? '0 0 0 3px rgba(229,72,61,0.12)' : 'none',
  });

  const sStyle = (key) => ({
    ...iStyle(key),
    appearance:'none',
    backgroundImage:`url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='11' height='7' fill='none' viewBox='0 0 11 7'%3E%3Cpath stroke='%235a6482' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M1 1l4.5 4.5L10 1'/%3E%3C/svg%3E")`,
    backgroundRepeat:'no-repeat',
    backgroundPosition:'right 12px center',
    paddingRight:34,
    cursor:'pointer',
  });

  const typeLabel = FIELD_TYPES.flatMap((g) => g.options).find((o) => o.value === ft)?.label || ft;
  const divider   = <div style={{ height:1, background:'var(--cf-border,#e4e7ef)', margin:'16px 0' }} />;

  // ─────────────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div
      style={{ position:'fixed', inset:0, background:'rgba(14,18,48,0.55)', backdropFilter:'blur(5px)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:999990, padding:20, animation:'cfm-bg-in .18s ease' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <style>{`
        @keyframes cfm-bg-in { from{opacity:0} to{opacity:1} }
        @keyframes cfm-in    { from{opacity:0;transform:translateY(18px) scale(.97)} to{opacity:1;transform:none} }
        @keyframes cfm-spin  { to{transform:rotate(360deg)} }
        .cfm-input:focus,.cfm-select:focus,.cfm-ta:focus { border-color:var(--cf-border-focus,#6c8ef4)!important; box-shadow:0 0 0 3px rgba(108,142,244,.15)!important; }
        .cfm-opt-del:hover { background:#fff0f1!important; border-color:#e5484d!important; color:#e5484d!important; }
        .cfm-modal-body::-webkit-scrollbar { width:4px; }
        .cfm-modal-body::-webkit-scrollbar-thumb { background:var(--cf-border,#e4e7ef); border-radius:4px; }
        .cfm-close-btn:hover { background:var(--cf-danger-light,#fff0f1)!important; border-color:var(--cf-danger,#f04c5a)!important; color:var(--cf-danger,#f04c5a)!important; }
      `}</style>

      {/* ── Shell ─────────────────────────────────────────────────────────── */}
      <div style={{ background:'var(--cf-surface,#fff)', border:'1px solid var(--cf-border,#e4e7ef)', borderRadius:14, boxShadow:'0 28px 90px rgba(14,18,48,0.24),0 4px 16px rgba(14,18,48,0.1)', width:'100%', maxWidth:620, maxHeight:'92vh', display:'flex', flexDirection:'column', animation:'cfm-in .22s cubic-bezier(.34,1.08,.64,1)', overflow:'hidden' }}>

        {/* ── Modal header ─────────────────────────────────────────────────── */}
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'18px 22px 16px', borderBottom:'1px solid var(--cf-border,#e4e7ef)', flexShrink:0 }}>
          <div style={{ display:'flex', alignItems:'center', gap:12 }}>
            {/* Icon */}
            <div style={{ width:38, height:38, borderRadius:10, background: isEdit ? '#fff3e0' : 'var(--cf-accent-light,#eef1ff)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
              {isEdit ? (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#e67e22" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                </svg>
              ) : (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--cf-accent,#4f6ef7)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                  <polyline points="14 2 14 8 20 8"/>
                  <line x1="12" y1="18" x2="12" y2="12"/>
                  <line x1="9"  y1="15" x2="15" y2="15"/>
                </svg>
              )}
            </div>
            <div>
              <h2 style={{ margin:0, fontSize:15, fontWeight:700, color:'var(--cf-text-primary,#1a1f36)' }}>
                {isEdit ? 'Edit Registration Field' : 'Add Registration Field'}
              </h2>
              <p style={{ margin:'2px 0 0', fontSize:12, color:'var(--cf-text-muted,#8c96b5)' }}>
                {isEdit ? 'Update settings for this field' : 'Creates a new'}&nbsp;
                <span style={{ display:'inline-flex', alignItems:'center', background: isEdit ? '#fff3e0' : 'var(--cf-accent-light,#eef1ff)', color: isEdit ? '#e67e22' : 'var(--cf-accent,#4f6ef7)', borderRadius:20, padding:'1px 8px', fontSize:11, fontWeight:700 }}>
                  {typeLabel}
                </span>
                {!isEdit && ' field on the registration form'}
              </p>
            </div>
          </div>

          {/* Close */}
          <button type="button" className="cfm-close-btn" onClick={onClose}
            style={{ width:30, height:30, borderRadius:7, border:'1.5px solid var(--cf-border,#e4e7ef)', background:'transparent', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', color:'var(--cf-text-muted,#8c96b5)', flexShrink:0, transition:'all .14s' }}
            aria-label="Close">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        {/* ── Global error ──────────────────────────────────────────────────── */}
        {errors._global && (
          <div style={{ margin:'12px 22px 0', padding:'10px 14px', background:'#fff0f0', border:'1.5px solid #fccfcf', borderRadius:8, fontSize:13, color:'#c0303c', fontWeight:600, display:'flex', alignItems:'center', gap:8, flexShrink:0 }}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
            {errors._global}
          </div>
        )}

        {/* ── Loading state ─────────────────────────────────────────────────── */}
        {loadingData ? (
          <div style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center', gap:10, color:'var(--cf-text-muted,#8c96b5)', fontSize:13 }}>
            <Spin />
            Loading field data…
          </div>
        ) : (

          /* ── Scrollable body ──────────────────────────────────────────────── */
          <div className="cfm-modal-body" style={{ overflowY:'auto', flex:1, padding:'20px 22px' }}>

            {/* Field Title removed — the single Field Label input now drives title and slug */}

            {/* Enable */}
            <div style={{ marginBottom:16 }}>
              <Lbl htmlFor="cfm-enable">Field Enable</Lbl>
              <div style={{ padding:'9px 13px', background:'var(--cf-surface-soft,#f9fafc)', border:'1.5px solid var(--cf-border,#e4e7ef)', borderRadius:7, display:'flex', alignItems:'center', gap:10 }}>
                <Toggle id="cfm-enable" checked={form.custom_field_checkbox === 'yes'} onChange={(v) => set('custom_field_checkbox', v ? 'yes' : 'no')} />
                <label htmlFor="cfm-enable" style={{ fontSize:13, fontWeight:500, cursor:'pointer', userSelect:'none', color:'var(--cf-text-primary,#1a1f36)' }}>
                  {form.custom_field_checkbox === 'yes' ? 'Enabled' : 'Disabled'}
                </label>
              </div>
            </div>

            {/* Field Type */}
            <div style={{ marginBottom:16 }}>
              <Lbl htmlFor="cfm-type" required>Field Type</Lbl>
              <select id="cfm-type" className="cfm-select" style={{ ...sStyle('custom_register_field_type'), width: '40%' }} value={ft}
                // onChange={(e) => { set('custom_register_field_type', e.target.value); if (!isEdit) setSlugTouched(false); }}
                onChange={(e) => {
                  const val = e.target.value;
                  const selectedOption = FIELD_TYPES
                    .flatMap((g) => g.options)
                    .find((o) => o.value === val);

                  // Block pro selection
                  if (selectedOption?.pro) {
                    return;
                  }
                  if (isBillingOrShipping(val)) {
                      const typeLabel = FIELD_TYPES.flatMap((g) => g.options).find((o) => o.value === val)?.label || val;
                      setForm((f) => ({ ...f, custom_register_field_type: val, post_title: typeLabel }));
                      setErrors((e) => ({ ...e, custom_register_field_type: '', post_title: '' }));
                  } else {
                      set('custom_register_field_type', val);
                  }
                  if (!isEdit) setSlugTouched(false);
                }}>
                {/* {FIELD_TYPES.map((g) => (
                  <optgroup key={g.group} label={g.group}>
                    {g.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </optgroup>
                ))} */}
                {FIELD_TYPES.map((g) => (
                  <optgroup key={g.group} label={g.group}>
                    {g.options.map((o) => (
                      <option key={o.value} value={o.value} disabled={!!o.pro}>
                        {o.label}{o.pro ? ' 🔒' : ''}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>

              <span style={{fontSize: '13px', fontWeight: 600, marginLeft: '10px'}}>
                Some Options Are Only Available In{' '}
                <a
                  className="cf-pro-link"
                  href="https://www.plugin999.com/plugin/custom-fields-account-registration-for-woocommerce/"
                >
                  Pro Version
                </a>
              </span>
            </div>

            {/* ── Label + Slug + Required ─── */}
            {needsLabelAndSlug(ft) && (
              <>
                {divider}
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14, marginBottom:16 }}>
                  <div>
                    <Lbl htmlFor="cfm-label" required>Field Label</Lbl>
                    <input id="cfm-label" type="text" className="cfm-input" style={iStyle('custom_field_label')} placeholder="e.g. Company Name" value={form.custom_field_label} autoFocus onChange={(e) => handleLabelChange(e.target.value)} />
                    <Err msg={errors.custom_field_label} />
                  </div>
                  <div>
                    <Lbl htmlFor="cfm-slug" required>Slug Name</Lbl>
                    <input id="cfm-slug" type="text" className="cfm-input" style={iStyle('custom_field_slug_name')} placeholder="e.g. company_name" value={form.custom_field_slug_name} onChange={(e) => handleSlugChange(e.target.value)} />
                    {errors.custom_field_slug_name
                      ? <Err msg={errors.custom_field_slug_name} />
                      : <p style={{ margin:'4px 0 0', fontSize:11.5, color:'var(--cf-text-muted,#8c96b5)' }}>Format: <code style={{ background:'#f0f2f8', padding:'1px 5px', borderRadius:4, fontSize:11 }}>slug_name</code></p>
                    }
                  </div>
                </div>

                {/* Required toggle */}
                {/* <div style={{ marginBottom:16 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:10, padding:'9px 13px', background:'var(--cf-surface-soft,#f9fafc)', border:'1.5px solid var(--cf-border,#e4e7ef)', borderRadius:7 }}>
                    <Toggle id="cfm-required" checked={form.custom_field_required === 'yes'} onChange={(v) => set('custom_field_required', v ? 'yes' : '')} />
                    <label htmlFor="cfm-required" style={{ fontSize:13, fontWeight:500, cursor:'pointer', userSelect:'none', color:'var(--cf-text-primary,#1a1f36)' }}>Field Required</label>
                    <span style={{ marginLeft:'auto', fontSize:12, color:'var(--cf-text-muted,#8c96b5)' }}>Marks field as mandatory on the registration form</span>
                  </div>
                </div> */}
              </>
            )}

            {!isHtmlType(ft) && (
              <div style={{ marginBottom: 16 }}>
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '9px 13px',
                  background: 'var(--cf-surface-soft,#f9fafc)',
                  border: '1.5px solid var(--cf-border,#e4e7ef)',
                  borderRadius: 7,
                }}>
                  <Toggle
                    id="cfm-required"
                    checked={form.custom_field_required === 'yes'}
                    onChange={(v) => set('custom_field_required', v ? 'yes' : '')}
                  />
                  <label htmlFor="cfm-required" style={{ fontSize: 13, fontWeight: 500, cursor: 'pointer', userSelect: 'none', color: 'var(--cf-text-primary,#1a1f36)' }}>
                    Field Required
                  </label>
                  <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--cf-text-muted,#8c96b5)' }}>
                    Marks field as mandatory on the registration form
                  </span>
                </div>
              </div>
            )}

            {/* ── Placeholder ─── */}
            {needsPlaceholder(ft) && (
              <div style={{ marginBottom:16 }}>
                <Lbl htmlFor="cfm-ph" optional>Field Placeholder</Lbl>
                <input id="cfm-ph" type="text" className="cfm-input" style={iStyle('custom_field_placeholder')} placeholder="e.g. Enter your company name…" value={form.custom_field_placeholder} onChange={(e) => set('custom_field_placeholder', e.target.value)} />
              </div>
            )}

            {/* ── Options (radio/select/multiselect/multicheckbox) ─── */}
            {needsOptions(ft) && (
              <div style={{ marginBottom:16 }}>
                {divider}
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:10 }}>
                  <Lbl>Field Options</Lbl>
                  <button type="button" className="cf-btn cf-btn--outline cf-btn--sm" onClick={addOption} style={{ marginBottom:0 }}>
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                      <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
                    </svg>
                    Add Option
                  </button>
                </div>

                {/* Col headers */}
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 32px', gap:8, marginBottom:6 }}>
                  {['Value','Label',''].map((h,i) => <span key={i} style={{ fontSize:11, fontWeight:700, color:'var(--cf-text-muted,#8c96b5)', textTransform:'uppercase', letterSpacing:'.4px' }}>{h}</span>)}
                </div>

                {/* Rows */}
                <div style={{ display:'flex', flexDirection:'column', gap:7 }}>
                  {form.custom_field_option_value.map((val, i) => (
                    <div key={i} style={{ display:'grid', gridTemplateColumns:'1fr 1fr 32px', gap:8, alignItems:'center' }}>
                      <input type="text" className="cfm-input" style={{ ...iStyle(), padding:'7px 10px', fontSize:13 }} placeholder="value" value={val} onChange={(e) => setOption(i, 'custom_field_option_value', e.target.value)} />
                      <input type="text" className="cfm-input" style={{ ...iStyle(), padding:'7px 10px', fontSize:13 }} placeholder="label" value={form.custom_field_option_label[i] || ''} onChange={(e) => setOption(i, 'custom_field_option_label', e.target.value)} />
                      <button type="button" className="cfm-opt-del" onClick={() => removeOption(i)} disabled={form.custom_field_option_value.length <= 1}
                        style={{ width:30, height:30, display:'flex', alignItems:'center', justifyContent:'center', border:'1.5px solid var(--cf-border,#e4e7ef)', borderRadius:6, background:'var(--cf-surface,#fff)', cursor: form.custom_field_option_value.length <= 1 ? 'not-allowed' : 'pointer', color:'var(--cf-text-muted,#8c96b5)', opacity: form.custom_field_option_value.length <= 1 ? .4 : 1, transition:'all .14s' }}>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                      </button>
                    </div>
                  ))}
                </div>
                <p style={{ margin:'7px 0 0', fontSize:12, color:'var(--cf-text-muted,#8c96b5)' }}>"Value" is stored in the database; "Label" is shown to users on the form.</p>
              </div>
            )}

            {/* ── Custom HTML (html type) ─── */}
            {isHtmlType(ft) && (
              <>
              <div style={{ marginBottom: 16 }}>
                <Lbl htmlFor="cfm-html-title" required>Field Name</Lbl>
                <input
                  id="cfm-html-title"
                  type="text"
                  className="cfm-input"
                  style={iStyle('post_title')}
                  placeholder="e.g. My HTML Block"
                  value={form.post_title}
                  onChange={(e) => set('post_title', e.target.value)}
                />
                <Err msg={errors.post_title} />
              </div>

              <div style={{ marginBottom:16 }}>
                <Lbl htmlFor="cfm-html" required>Custom HTML</Lbl>
                <textarea id="cfm-html" className="cfm-ta" style={{ ...iStyle('cfafwr_custom_html'), resize:'vertical', minHeight:100, fontFamily:'monospace', fontSize:12.5, lineHeight:1.6 }} placeholder="<div class='my-block'>…</div>" rows={5} value={form.cfafwr_custom_html} onChange={(e) => set('cfafwr_custom_html', e.target.value)} />
                <Err msg={errors.cfafwr_custom_html} />
              </div>
              </>
            )}

           

          </div>
        )}

        {/* ── Footer ───────────────────────────────────────────────────────── */}
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'14px 22px', borderTop:'1px solid var(--cf-border,#e4e7ef)', background:'var(--cf-surface-soft,#f9fafc)', flexShrink:0, gap:12, flexWrap:'wrap' }}>
          <p style={{ margin:0, fontSize:12, color:'var(--cf-text-muted,#8c96b5)', display:'flex', alignItems:'center', gap:5 }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
            {isEdit ? 'Changes save instantly — no page reload needed' : 'Field appears at the bottom of the list after saving'}
          </p>
          <div style={{ display:'flex', gap:9, marginLeft:'auto' }}>
            <button type="button" className="cf-btn cf-btn--outline" onClick={onClose} disabled={saving || loadingData}>Cancel</button>
            <button type="button" className="cf-btn cf-btn--primary" onClick={handleSave} disabled={saving || loadingData}>
              {saving ? (
                <><Spin />Saving…</>
              ) : isEdit ? (
                <>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/>
                    <polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/>
                  </svg>
                  Update Field
                </>
              ) : (
                <>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/>
                    <polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/>
                  </svg>
                  Save Field
                </>
              )}
            </button>
          </div>
        </div>

      </div>
    </div>
  );
}
